'use client';

import { createContext, useContext, useEffect, useRef } from 'react';
import ConversionArea, { ConversionAreaProps } from './conversion-area';
import CtaButton, { CtaButtonProps } from './cta-button';

const WordpressComponentContext = createContext<{
  getAndIncrementConversionAreaIndex: () => number;
  getAndIncrementCtaButtonIndex: () => number;
} | null>(null);

function useWordpressComponentContext() {
  const context = useContext(WordpressComponentContext);
  if (!context) {
    throw new Error(
      'useWordpressComponentContext must be used within a WordpressComponentWrapper',
    );
  }
  return context;
}

export function WordpressComponentWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const conversionAreaIndex = useRef(0);
  const ctaButtonIndex = useRef(0);

  const getAndIncrementConversionAreaIndex = () => {
    const currentIndex = conversionAreaIndex.current;
    conversionAreaIndex.current++;
    return currentIndex;
  };

  const getAndIncrementCtaButtonIndex = () => {
    const currentIndex = ctaButtonIndex.current;
    ctaButtonIndex.current++;
    return currentIndex;
  };

  const contextValue = {
    getAndIncrementConversionAreaIndex,
    getAndIncrementCtaButtonIndex,
  };

  useEffect(() => {
    return () => {
      conversionAreaIndex.current = 0;
      ctaButtonIndex.current = 0;
    };
  }, []);

  return (
    <WordpressComponentContext.Provider value={contextValue}>
      {children}
    </WordpressComponentContext.Provider>
  );
}

export function IndexedConversionArea(
  props: Omit<ConversionAreaProps, 'index'>,
) {
  const { getAndIncrementConversionAreaIndex } = useWordpressComponentContext();
  const index = getAndIncrementConversionAreaIndex();
  return <ConversionArea {...props} index={index} />;
}

export function IndexedCtaButton(props: Omit<CtaButtonProps, 'index'>) {
  const { getAndIncrementCtaButtonIndex } = useWordpressComponentContext();
  const index = getAndIncrementCtaButtonIndex();
  return <CtaButton {...props} index={index} />;
}
